// components/TicketList.jsx
import React, { useState } from "react";

const TicketList = ({ tickets, onViewTicket, onDeleteTicket }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const titleCase = (s) =>
    typeof s === "string" && s.length
      ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
      : "";

  const STATUS_CONFIG = {
    Pending:       { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
    Open:          { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
    "In Progress": { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
    Closed:        { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
    Resolved:      { color: "#374151", bg: "#f3f4f6", border: "#e5e7eb" },
  };

  const PRIORITY_CONFIG = {
    High:   { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
    Medium: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
    Low:    { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  };

  const STATUS_DOT = {
    Pending: "#d97706",
    Open: "#2563eb",
    "In Progress": "#d97706",
    Closed: "#16a34a",
    Resolved: "#6b7280",
  };

  const Pill = ({ cfg, label, dotColor }) => (
    <span
      className="tl-pill d-inline-flex align-items-center"
      style={{
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
      }}
    >
      {dotColor && <span className="tl-pill-dot" style={{ background: dotColor }} />}
      {label}
    </span>
  );

  const getPriorityBadge = (priorityRaw) => {
    const priority = titleCase(priorityRaw) || "Medium";
    const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.Medium;
    return <Pill cfg={cfg} label={priority} />;
  };

  const getStatusBadge = (statusRaw) => {
    const status = statusRaw || "Pending";
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Pending;
    return <Pill cfg={cfg} label={status} dotColor={STATUS_DOT[status] || "#6b7280"} />;
  };

  const categories = ["All", ...new Set(tickets.map((t) => t.category))];
  const priorities = ["All", "High", "Medium", "Low"];
  const statuses = ["All", "Open", "In Progress", "Closed"];

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || ticket.status === statusFilter;
    const matchesPriority =
      priorityFilter === "All" ||
      titleCase(ticket.priority) === priorityFilter;
    const matchesCategory = categoryFilter === "All" || ticket.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, startIndex + itemsPerPage);

  const handleFirstPage = () => setCurrentPage(1);
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handleLastPage = () => setCurrentPage(totalPages);
  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);

  const hasActiveFilters =
    statusFilter !== "All" ||
    priorityFilter !== "All" ||
    categoryFilter !== "All" ||
    searchTerm;

  const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="tl-card">
      {/* Header */}
      <div className="tl-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <div className="tl-card-eyebrow">All tickets</div>
          <p className="tl-card-meta mb-0">
            <span className="tl-card-meta-strong">{filteredTickets.length}</span>{" "}
            tickets found
            <span className="tl-meta-divider" aria-hidden>·</span>
            <span className="tl-card-meta-strong">
              {tickets.filter((t) => t.status === "Open").length}
            </span>{" "}
            require attention
          </p>
        </div>
      </div>

      <div className="tl-card-body">
        {/* Filters */}
        <div className="tl-filters d-flex flex-wrap gap-2 mb-3">
          <div className="tl-search position-relative flex-grow-1">
            <span className="tl-search-icon d-flex align-items-center justify-content-center" aria-hidden>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className="tl-search-input"
              placeholder="Search tickets by ID, subject, or description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="tl-search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="tl-select-wrap">
            <select
              className="tl-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === "All" ? "All status" : status}
                </option>
              ))}
            </select>
          </div>

          <div className="tl-select-wrap">
            <select
              className="tl-select"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              {priorities.map((priority) => (
                <option key={priority} value={priority}>
                  {priority === "All" ? "All priorities" : priority}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            <span className="tl-active-label">Active:</span>
            {searchTerm && (
              <span className="tl-chip tl-chip--accent">
                Search: "{searchTerm}"
                <button
                  type="button"
                  className="tl-chip-remove"
                  onClick={() => setSearchTerm("")}
                  aria-label="Remove search filter"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            )}
            {statusFilter !== "All" && (
              <span className="tl-chip">
                Status: {statusFilter}
                <button
                  type="button"
                  className="tl-chip-remove"
                  onClick={() => setStatusFilter("All")}
                  aria-label="Remove status filter"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            )}
            {priorityFilter !== "All" && (
              <span className="tl-chip">
                Priority: {priorityFilter}
                <button
                  type="button"
                  className="tl-chip-remove"
                  onClick={() => setPriorityFilter("All")}
                  aria-label="Remove priority filter"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            )}
            {categoryFilter !== "All" && (
              <span className="tl-chip">
                Category: {categoryFilter}
                <button
                  type="button"
                  className="tl-chip-remove"
                  onClick={() => setCategoryFilter("All")}
                  aria-label="Remove category filter"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            )}
            <button
              type="button"
              className="tl-clear-all"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("All");
                setPriorityFilter("All");
                setCategoryFilter("All");
              }}
            >
              Clear all
            </button>
          </div>
        )}

        {/* Table or empty */}
        {filteredTickets.length > 0 ? (
          <div className="tl-table-wrap">
            <table className="tl-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTickets.map((ticket) => (
                  <tr key={ticket.id} className="tl-row">
                    <td>
                      <div
                        className="d-flex align-items-center gap-2 tl-row-clickable"
                        onClick={() => onViewTicket(ticket)}
                      >
                        <span className="tl-row-icon d-inline-flex align-items-center justify-content-center">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 10v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6" />
                            <path d="M22 10a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                          </svg>
                        </span>
                        <span className="tl-row-id">{ticket.ticket_id}</span>
                      </div>
                    </td>
                    <td>{getPriorityBadge(ticket.priority)}</td>
                    <td>{getStatusBadge(ticket.status)}</td>
                    <td className="tl-row-date">{formatDate(ticket.createdAt)}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="tl-icon-btn"
                        title="View"
                        onClick={() => onViewTicket(ticket)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="tl-empty d-flex flex-column align-items-center text-center">
            <div className="tl-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
              </svg>
            </div>
            <h5 className="tl-empty-title">No tickets found</h5>
            {hasActiveFilters && (
              <p className="tl-empty-text mb-0">
                Try adjusting your filters or{" "}
                <button
                  type="button"
                  className="tl-link-btn"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("All");
                    setPriorityFilter("All");
                    setCategoryFilter("All");
                  }}
                >
                  clear all
                </button>
                .
              </p>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredTickets.length > 0 && (
        <div className="tl-card-footer d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="tl-page-info">
            Showing <span className="tl-page-info-strong">{startIndex + 1}</span>–
            <span className="tl-page-info-strong">{Math.min(startIndex + itemsPerPage, filteredTickets.length)}</span>{" "}
            of <span className="tl-page-info-strong">{filteredTickets.length}</span> tickets
          </div>
          {totalPages > 1 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="tl-page-btn"
                title="First page"
                onClick={handleFirstPage}
                disabled={currentPage === 1}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="11 17 6 12 11 7" />
                  <polyline points="18 17 13 12 18 7" />
                </svg>
              </button>
              <button
                type="button"
                className="tl-page-btn"
                title="Previous page"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>

              <div className="d-flex align-items-center gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`tl-page-num ${currentPage === pageNum ? "is-active" : ""}`}
                        onClick={() => handlePageClick(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return (
                      <span key={index} className="tl-page-ellipsis" aria-hidden>
                        …
                      </span>
                    );
                  }
                  return null;
                })}
              </div>

              <button
                type="button"
                className="tl-page-btn"
                title="Next page"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <button
                type="button"
                className="tl-page-btn"
                title="Last page"
                onClick={handleLastPage}
                disabled={currentPage === totalPages}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="13 17 18 12 13 7" />
                  <polyline points="6 17 11 12 6 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        .tl-card {
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 14px;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
        }
        .tl-card-header {
          padding: 14px 20px;
          border-bottom: 1px solid #f1f1f1;
          background: #fafafa;
        }
        .tl-card-eyebrow {
          font-size: 0.6875rem;
          font-weight: 600;
          color: #6b7280;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .tl-card-meta {
          font-size: 0.8125rem;
          color: #6b7280;
        }
        .tl-card-meta-strong { color: #1f2937; font-weight: 600; }
        .tl-meta-divider { margin: 0 6px; color: #d1d5db; }
        .tl-card-body { padding: 18px 20px; }
        .tl-card-footer {
          padding: 14px 20px;
          border-top: 1px solid #f1f1f1;
          background: #fafafa;
        }

        /* Filters */
        .tl-filters > .tl-search { min-width: 240px; }
        .tl-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          pointer-events: none;
        }
        .tl-search-input {
          width: 100%;
          height: 38px;
          padding: 0 36px 0 36px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          font-family: inherit;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .tl-search-input::placeholder { color: #9ca3af; }
        .tl-search-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .tl-search-clear {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          width: 26px;
          height: 26px;
          border: 0;
          background: transparent;
          color: #6b7280;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .tl-search-clear:hover { background: #f3f4f6; color: #1f2937; }

        .tl-select-wrap { min-width: 170px; }
        .tl-select {
          width: 100%;
          height: 38px;
          padding: 0 32px 0 12px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          font-family: inherit;
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 10px center;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          cursor: pointer;
        }
        .tl-select:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .tl-select:hover { border-color: #d1d5db; }

        /* Active chips */
        .tl-active-label {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
          letter-spacing: 0.01em;
        }
        .tl-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 4px 4px 10px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #374151;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 999px;
          line-height: 1.4;
        }
        .tl-chip--accent {
          color: #4338ca;
          background: #eef2ff;
          border-color: #e0e7ff;
        }
        .tl-chip-remove {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: rgba(17, 24, 39, 0.06);
          color: #4b5563;
          border: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .tl-chip--accent .tl-chip-remove {
          background: rgba(79, 70, 229, 0.1);
          color: #4f46e5;
        }
        .tl-chip-remove:hover {
          background: #1f2937;
          color: #fff;
        }
        .tl-chip--accent .tl-chip-remove:hover {
          background: #4f46e5;
          color: #fff;
        }
        .tl-clear-all {
          font-size: 0.75rem;
          font-weight: 600;
          color: #4f46e5;
          background: transparent;
          border: 0;
          padding: 4px 8px;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.15s ease;
        }
        .tl-clear-all:hover { background: #eef2ff; color: #4338ca; }

        /* Pills (status / priority) */
        .tl-pill {
          padding: 3px 10px;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          border-radius: 999px;
          line-height: 1.45;
          white-space: nowrap;
        }
        .tl-pill-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-right: 6px;
          display: inline-block;
        }

        /* Table */
        .tl-table-wrap {
          width: 100%;
          overflow-x: auto;
          border: 1px solid #ececec;
          border-radius: 12px;
        }
        .tl-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .tl-table thead th {
          background: #fafafa;
          color: #6b7280;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid #ececec;
        }
        .tl-table tbody td {
          padding: 14px 16px;
          border-bottom: 1px solid #f3f4f6;
          color: #1f2937;
          vertical-align: middle;
        }
        .tl-table tbody tr:last-child td { border-bottom: 0; }
        .tl-row {
          transition: background 0.12s ease;
        }
        .tl-row:hover { background: #fafafa; }
        .tl-row-clickable { cursor: pointer; }
        .tl-row-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: #eef2ff;
          color: #4f46e5;
        }
        .tl-row-clickable:hover .tl-row-icon {
          background: #e0e7ff;
        }
        .tl-row-id {
          font-weight: 600;
          color: #1f2937;
          font-size: 0.875rem;
          letter-spacing: 0.005em;
        }
        .tl-row-date {
          font-size: 0.8125rem;
          color: #6b7280;
        }

        .tl-icon-btn {
          width: 30px;
          height: 30px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #4b5563;
          border-radius: 7px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.2s ease;
        }
        .tl-icon-btn:hover {
          background: #fafafa;
          border-color: #d1d5db;
          color: #4f46e5;
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05);
        }
        .tl-icon-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }

        /* Empty */
        .tl-empty {
          padding: 56px 24px;
          border: 1px dashed #e5e5e5;
          border-radius: 12px;
          background: #fafafa;
        }
        .tl-empty-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #fff;
          border: 1px solid #ececec;
          color: #9ca3af;
        }
        .tl-empty-title {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px;
        }
        .tl-empty-text {
          font-size: 0.8125rem;
          color: #6b7280;
        }
        .tl-link-btn {
          background: transparent;
          border: 0;
          padding: 0;
          color: #4f46e5;
          font: inherit;
          cursor: pointer;
        }
        .tl-link-btn:hover { color: #4338ca; text-decoration: underline; }

        /* Pagination */
        .tl-page-info {
          font-size: 0.8125rem;
          color: #6b7280;
        }
        .tl-page-info-strong { color: #1f2937; font-weight: 600; }

        .tl-page-btn {
          width: 32px;
          height: 32px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #4b5563;
          border-radius: 7px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .tl-page-btn:hover:not(:disabled) {
          background: #fafafa;
          border-color: #d1d5db;
          color: #1f2937;
        }
        .tl-page-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .tl-page-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }

        .tl-page-num {
          min-width: 32px;
          height: 32px;
          padding: 0 8px;
          border: 1px solid transparent;
          background: transparent;
          color: #4b5563;
          font-size: 0.8125rem;
          font-weight: 500;
          border-radius: 7px;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .tl-page-num:hover {
          background: #fafafa;
          color: #1f2937;
        }
        .tl-page-num.is-active {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          font-weight: 600;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22);
        }
        .tl-page-num:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .tl-page-ellipsis {
          color: #9ca3af;
          padding: 0 4px;
          font-size: 0.875rem;
          user-select: none;
        }
      `}</style>
    </div>
  );
};

export default TicketList;
